<template>
  <el-dialog
    :model-value="visible"
    class="help-article-dialog"
    width="860px"
    top="8vh"
    append-to-body
    destroy-on-close
    :close-on-click-modal="false"
    @update:model-value="onVisibleChange"
    @closed="handleClosed"
  >
    <template #title>
      <div class="dialog-header">
        <span class="dialog-title">帮助说明</span>
        <el-button link type="primary" :disabled="!article?.id" @click="openInHelpCenter">
          在帮助中心打开
        </el-button>
      </div>
    </template>

    <div v-loading="loading" class="dialog-body">
      <template v-if="article">
        <div class="article-head">
          <h2>{{ article.title }}</h2>
          <div class="article-meta">
            <el-tag size="small">{{ article.categoryName || '未分类' }}</el-tag>
            <span>浏览 {{ article.viewCount || 0 }}</span>
            <span v-if="article.updateTime">更新于 {{ article.updateTime }}</span>
          </div>
        </div>
        <div class="article-layout">
          <div
            ref="contentRef"
            class="article-content"
            v-html="articleContentHtml"
            @click="onContentClick"
            @scroll="onContentScroll"
          />
          <aside v-if="anchors.length" class="article-toc">
            <div class="toc-title">本页大纲</div>
            <a
              v-for="a in anchors"
              :key="a.id"
              class="toc-item"
              :class="[`level-${a.level}`, { active: activeAnchor === a.id }]"
              href="javascript:;"
              @click="scrollToAnchor(a.id)"
            >
              {{ a.text || a.id }}
            </a>
          </aside>
        </div>
      </template>
      <el-empty v-else-if="!loading" description="暂无对应帮助内容" />
    </div>

  </el-dialog>

  <!-- 挂到 body，避免受 Dialog transform 影响定位 -->
  <Teleport to="body">
    <div
      v-if="imagePreviewVisible"
      class="image-preview-overlay"
      @click="closeImagePreview"
    >
      <div class="image-preview-toolbar">
        <span class="image-preview-counter">
          {{ imagePreviewIndex + 1 }} / {{ imagePreviewList.length }}
        </span>
        <el-button circle size="small" :disabled="previewScale <= 0.3" @click.stop="zoomOut">
          <el-icon><ZoomOut /></el-icon>
        </el-button>
        <el-button circle size="small" :disabled="previewScale >= 5" @click.stop="zoomIn">
          <el-icon><ZoomIn /></el-icon>
        </el-button>
        <el-button circle size="small" @click.stop="closeImagePreview">
          <el-icon><Close /></el-icon>
        </el-button>
      </div>
      <button
        class="preview-arrow prev"
        :disabled="imagePreviewList.length <= 1"
        @click.stop="previewPrev"
      >
        <el-icon><ArrowLeft /></el-icon>
      </button>
      <img
        :src="imagePreviewUrl"
        :style="{ transform: `scale(${previewScale})` }"
        class="image-preview-img"
        @click.stop
        @wheel.prevent="onPreviewWheel"
      />
      <button
        class="preview-arrow next"
        :disabled="imagePreviewList.length <= 1"
        @click.stop="previewNext"
      >
        <el-icon><ArrowRight /></el-icon>
      </button>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { ArrowLeft, ArrowRight, Close, ZoomIn, ZoomOut } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import {
  getHelpArticleByKey,
  type HelpAnchor,
  type HelpArticle
} from '@/common/apis/help'
import { useHelpArticleDialog } from '@/common/composables/useHelpArticleDialog'
import { resolveHtmlResourceUrls } from '@/common/config'
import { isApiSuccessCode } from '@/common/utils/api-response'
import { emitClosePublishSettings } from '@/common/utils/publish-settings-bus'

const router = useRouter()
const { visible, helpKey, close } = useHelpArticleDialog()

const loading = ref(false)
const article = ref<HelpArticle | null>(null)
const contentRef = ref<HTMLElement>()
const activeAnchor = ref('')
/** 防止快速切换帮助键时旧请求覆盖新结果 */
let loadSeq = 0

const imagePreviewVisible = ref(false)
const imagePreviewUrl = ref('')
const imagePreviewList = ref<string[]>([])
const imagePreviewIndex = ref(0)
const previewScale = ref(1)

const anchors = computed<HelpAnchor[]>(() => article.value?.anchors || [])
const articleContentHtml = computed(() =>
  resolveHtmlResourceUrls(article.value?.contentHtml)
)

async function loadByKey(key: string) {
  if (!key) return
  const seq = ++loadSeq
  loading.value = true
  article.value = null
  activeAnchor.value = ''
  closeImagePreview()
  try {
    const res = await getHelpArticleByKey(key)
    if (seq !== loadSeq) return
    if (!isApiSuccessCode(res.data?.code) || !res.data?.data?.id) {
      ElMessage.warning(
        res.data?.message || '暂无对应帮助内容，请先在帮助内容管理中配置'
      )
      return
    }
    article.value = res.data.data
    await nextTick()
    if (seq !== loadSeq) return
    onContentScroll()
  } catch (e: any) {
    if (seq !== loadSeq) return
    ElMessage.error(e?.message || '打开帮助失败')
  } finally {
    if (seq === loadSeq) {
      loading.value = false
    }
  }
}

watch(
  () => [visible.value, helpKey.value] as const,
  ([isVisible, key]) => {
    if (isVisible && key) {
      loadByKey(key)
    }
  }
)

function onVisibleChange(val: boolean) {
  if (!val) close()
}

function handleClosed() {
  loadSeq += 1
  article.value = null
  activeAnchor.value = ''
  closeImagePreview()
}

function openInHelpCenter() {
  const id = article.value?.id
  if (id == null) return
  emitClosePublishSettings()
  close()
  router.push({
    path: '/help',
    query: { id: String(id) }
  })
}

function scrollToAnchor(id: string) {
  activeAnchor.value = id
  const root = contentRef.value
  if (!root) return
  const el = root.querySelector(`#${CSS.escape(id)}`) as HTMLElement | null
  if (el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
}

function collectImageUrls(): string[] {
  if (!contentRef.value) return []
  const imgs = contentRef.value.querySelectorAll('img')
  return Array.from(imgs)
    .map((img) => img.getAttribute('src') || '')
    .filter(Boolean)
}

function onContentClick(e: MouseEvent) {
  const target = e.target as HTMLElement
  if (target.tagName !== 'IMG') return
  const src = target.getAttribute('src')
  if (!src) return

  const list = collectImageUrls()
  const idx = list.indexOf(src)
  imagePreviewList.value = list
  imagePreviewIndex.value = idx >= 0 ? idx : 0
  imagePreviewUrl.value = src
  previewScale.value = 1
  imagePreviewVisible.value = true
}

function closeImagePreview() {
  imagePreviewVisible.value = false
  previewScale.value = 1
}

function previewPrev() {
  if (imagePreviewList.value.length <= 1) return
  const next = imagePreviewIndex.value - 1
  imagePreviewIndex.value =
    next < 0 ? imagePreviewList.value.length - 1 : next
  imagePreviewUrl.value = imagePreviewList.value[imagePreviewIndex.value]
  previewScale.value = 1
}

function previewNext() {
  if (imagePreviewList.value.length <= 1) return
  const next = imagePreviewIndex.value + 1
  imagePreviewIndex.value =
    next >= imagePreviewList.value.length ? 0 : next
  imagePreviewUrl.value = imagePreviewList.value[imagePreviewIndex.value]
  previewScale.value = 1
}

function zoomIn() {
  previewScale.value = Math.min(previewScale.value + 0.3, 5)
}

function zoomOut() {
  previewScale.value = Math.max(previewScale.value - 0.3, 0.3)
}

function onPreviewWheel(e: WheelEvent) {
  if (e.deltaY < 0) zoomIn()
  else zoomOut()
}

function onContentScroll() {
  if (!contentRef.value || !anchors.value.length) return
  const container = contentRef.value
  const containerTop = container.getBoundingClientRect().top
  const nodes = anchors.value
    .map(
      (a) =>
        container.querySelector(`#${CSS.escape(a.id)}`) as HTMLElement | null
    )
    .filter(Boolean) as HTMLElement[]
  let current = nodes[0]?.id || ''
  for (const n of nodes) {
    const rect = n.getBoundingClientRect()
    if (rect.top - containerTop <= 24) current = n.id
  }
  activeAnchor.value = current
}
</script>

<style scoped lang="scss">
.dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding-right: 28px;
}

.dialog-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.dialog-body {
  min-height: 280px;
  max-height: calc(84vh - 120px);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.article-head {
  margin-bottom: 12px;
  flex-shrink: 0;

  h2 {
    margin: 0 0 8px;
    font-size: 20px;
    color: #303133;
    line-height: 1.4;
  }

  .article-meta {
    display: flex;
    gap: 12px;
    color: #909399;
    font-size: 13px;
    align-items: center;
    flex-wrap: wrap;
  }
}

.article-layout {
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: 1fr 160px;
  gap: 16px;
  overflow: hidden;
}

.article-content {
  overflow: auto;
  line-height: 1.75;
  color: #303133;
  word-break: break-word;
  padding-right: 4px;

  :deep(img) {
    max-width: 100%;
    cursor: pointer;
    transition: opacity 0.2s;
  }

  :deep(img):hover {
    opacity: 0.85;
  }

  :deep(video) {
    max-width: 100%;
  }

  :deep(h2) {
    scroll-margin-top: 12px;
    font-size: 20px;
    margin: 20px 0 10px;
  }

  :deep(h3) {
    scroll-margin-top: 12px;
    font-size: 16px;
    margin: 16px 0 8px;
  }
}

.article-toc {
  overflow: auto;
  border-left: 2px solid #ebeef5;
  padding-left: 12px;

  .toc-title {
    font-size: 13px;
    font-weight: 600;
    color: #606266;
    margin-bottom: 8px;
  }

  .toc-item {
    display: block;
    color: #606266;
    text-decoration: none;
    font-size: 13px;
    padding: 4px 0;
    line-height: 1.4;

    &.level-3 {
      padding-left: 10px;
      font-size: 12px;
    }

    &.active,
    &:hover {
      color: #409eff;
    }
  }
}

@media (max-width: 960px) {
  .article-layout {
    grid-template-columns: 1fr;
  }

  .article-toc {
    border-left: none;
    border-top: 1px solid #ebeef5;
    padding-left: 0;
    padding-top: 12px;
    max-height: 140px;
  }
}

.image-preview-overlay {
  position: fixed;
  inset: 0;
  z-index: 4000;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: zoom-out;
}

.image-preview-toolbar {
  position: fixed;
  top: 12px;
  right: 16px;
  z-index: 4001;
  display: flex;
  align-items: center;
  gap: 8px;
}

.image-preview-counter {
  color: #fff;
  font-size: 14px;
  margin-right: 8px;
  user-select: none;
}

.image-preview-img {
  max-width: 90vw;
  max-height: 90vh;
  object-fit: contain;
  cursor: grab;
  transition: transform 0.15s ease;
  user-select: none;
}

.preview-arrow {
  position: fixed;
  top: 50%;
  z-index: 4001;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.15);
  border: none;
  border-radius: 50%;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #fff;
  font-size: 22px;
  transition: background 0.2s;

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.3);
  }

  &:disabled {
    opacity: 0.3;
    cursor: default;
  }

  &.prev {
    left: 16px;
  }

  &.next {
    right: 16px;
  }
}
</style>

<style lang="scss">
.help-article-dialog {
  border-radius: 8px;

  .el-dialog__header {
    margin-right: 0;
    border-bottom: 1px solid #ebeef5;
    padding: 16px 20px 12px;
  }

  .el-dialog__body {
    padding: 16px 20px 20px;
  }
}
</style>
